import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Pencil,
  Plus,
  Search,
  Tag,
  Trash2,
  User,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi, loyaltyApi, posApi } from '@/services/api';
import { createCustomerRouted, companyDb } from '@/offline';
import { useAuthStore } from '@/store/authStore';
import { isAdminOrManager, isCashierLevel, isCompanyAdmin } from '@/types';
import type { CustomerItemPrice, POSCustomer, PosSettings } from '@/types';
import ConfirmModal from '@/components/common/ConfirmModal';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  normalizePhone,
  sanitizePhoneInput,
  validatePhone,
} from '@/utils/mobileValidation';
import { formatCnicOrNtn } from '@/utils/cnicNtnUtils';
import { getCurrencySymbol } from '@/utils/currency';
import {
  getCountry,
  getRegionsForCountry,
  isPakistan,
  isGcc,
} from '@/data/locales';
import UkPostcodeLookup from '@/components/common/UkPostcodeLookup';

type FormErrors = {
  customer_name?: string;
  mobile?: string;
  email?: string;
  cnic?: string;
  opening_balance?: string;
  credit_limit?: string;
};

interface CityOption {
  name: string;
  city: string;
}

export interface CustomerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: POSCustomer | null;
  initialTab?: 'basic' | 'prices';
  initialEditPriceIndex?: number | null;
  onSaved?: (savedCustomer?: any) => void;
  onDeleted?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CustomerFormModal({
  isOpen,
  onClose,
  customer = null,
  initialTab = 'basic',
  initialEditPriceIndex = null,
  onSaved,
  onDeleted,
}: CustomerFormModalProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { role } = useAuthStore();
  const isAdmin = isCompanyAdmin(role);
  const canSetAccountLimit = isAdminOrManager(role);
  const editingCustomer = customer;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => posApi.deleteCustomer(editingCustomer!.name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      qc.invalidateQueries({ queryKey: ['posCustomersSearch'] });
      qc.invalidateQueries({ queryKey: ['posCustomerSelect'] });
      toast.success(t('customers.deletedSuccess', 'Customer deleted successfully'));
      setShowDeleteConfirm(false);
      onClose();
      onDeleted?.();
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete customer');
      setShowDeleteConfirm(false);
      setDeleteError(errMsg);
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data?.message;
    },
    enabled: isOpen,
  });
  const defaultCountry = profile?.country || 'GB';

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return (res.data?.message || []) as CityOption[];
    },
    enabled: isOpen,
  });

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      try {
        const res = await posApi.getPosSettings();
        return res.data?.message as PosSettings;
      } catch {
        return null;
      }
    },
    enabled: isOpen,
    staleTime: 60_000,
  });

  const showDeliveryPrice = Boolean(
    posSettings?.enable_delivery && posSettings?.delivery_pricing_mode !== 'runtime_charge'
  );

  const { data: catalogItemsRes } = useQuery({
    queryKey: ['catalogItemsForCustomerPricing'],
    queryFn: async () => {
      const res = await posApi.getItems({ limit: 500, include_inactive: 0 });
      return res.data?.message?.items || res.data?.message || [];
    },
    enabled: isOpen,
    staleTime: 60_000,
  });
  const catalogItems = Array.isArray(catalogItemsRes) ? catalogItemsRes : [];

  const { data: loyaltyFormState } = useQuery({
    queryKey: ['customer-form-loyalty', editingCustomer?.name],
    enabled: Boolean(isOpen && editingCustomer?.name && !editingCustomer?.is_walk_in),
    staleTime: 15_000,
    queryFn: async () => {
      const res = await loyaltyApi.getCustomerBalance(editingCustomer!.name);
      const msg = res.data?.message as {
        enrolled?: boolean | number;
        marketing_consent?: boolean | number;
      };
      return {
        enrolled: Boolean(msg?.enrolled),
        marketing_consent: Boolean(msg?.marketing_consent),
      };
    },
  });

  const emptyForm = () => ({
    customer_name: '',
    mobile: '',
    email: '',
    city: '',
    province: '',
    postal_code: '',
    country: defaultCountry,
    address: '',
    cnic: '',
    ntn: '',
    is_default: false,
    customer_type: 'Retailer' as 'Retailer' | 'Wholesale' | 'Distributor',
    credit_limit: '0',
    opening_balance: '',
    join_loyalty: false,
    marketing_consent: false,
    custom_prices: [] as CustomerItemPrice[],
  });

  const [activeCustomerTab, setActiveCustomerTab] = useState<'basic' | 'prices'>('basic');
  const [formData, setFormData] = useState(emptyForm);
  const [postcodeError, setPostcodeError] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Pricing tab state
  const [selectedItemCode, setSelectedItemCode] = useState('');
  const [selectedVariantCode, setSelectedVariantCode] = useState('');
  const [newCustomPrice, setNewCustomPrice] = useState('');
  const [newCustomDeliveryPrice, setNewCustomDeliveryPrice] = useState('');
  const [priceItemSearch, setPriceItemSearch] = useState('');
  const [priceError, setPriceError] = useState('');
  const [editingPriceIndex, setEditingPriceIndex] = useState<number | null>(null);

  const enrolledFromCustomer = Boolean(
    Number(editingCustomer?.loyalty_enrolled) ||
      editingCustomer?.loyalty_enrolled === true ||
      loyaltyFormState?.enrolled
  );
  const marketingFromCustomer = Boolean(
    Number(editingCustomer?.marketing_consent) ||
      editingCustomer?.marketing_consent === true ||
      loyaltyFormState?.marketing_consent
  );

  useEffect(() => {
    if (!isOpen) return;

    setActiveCustomerTab(initialTab);
    setErrors({});
    setPostcodeError('');
    setPriceError('');
    setPriceItemSearch('');

    if (editingCustomer) {
      const prices = (editingCustomer.custom_prices || []).map((p) => ({ ...p }));
      setFormData({
        customer_name: editingCustomer.customer_name || '',
        mobile: editingCustomer.mobile || '',
        email: editingCustomer.email || '',
        city: editingCustomer.city || '',
        province: editingCustomer.province || '',
        postal_code: editingCustomer.postal_code || '',
        country: defaultCountry,
        address: editingCustomer.address || '',
        cnic: editingCustomer.cnic || '',
        ntn: editingCustomer.ntn || '',
        is_default: Boolean(editingCustomer.is_default),
        customer_type: (editingCustomer.customer_type as any) || 'Retailer',
        credit_limit: String(editingCustomer.credit_limit ?? 0),
        opening_balance: '',
        join_loyalty: enrolledFromCustomer,
        marketing_consent: marketingFromCustomer,
        custom_prices: prices,
      });

      if (
        initialEditPriceIndex !== null &&
        initialEditPriceIndex >= 0 &&
        prices[initialEditPriceIndex]
      ) {
        const row = prices[initialEditPriceIndex];
        setEditingPriceIndex(initialEditPriceIndex);
        setSelectedItemCode(row.item);
        setSelectedVariantCode(row.variant || '');
        setNewCustomPrice(String(row.custom_price));
        setNewCustomDeliveryPrice(
          row.custom_delivery_price != null ? String(row.custom_delivery_price) : ''
        );
      } else {
        setEditingPriceIndex(null);
        setSelectedItemCode('');
        setSelectedVariantCode('');
        setNewCustomPrice('');
        setNewCustomDeliveryPrice('');
      }
    } else {
      setFormData(emptyForm());
      setEditingPriceIndex(null);
      setSelectedItemCode('');
      setSelectedVariantCode('');
      setNewCustomPrice('');
      setNewCustomDeliveryPrice('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync when loyalty API resolves enrolled state
  }, [
    isOpen,
    editingCustomer?.name,
    defaultCountry,
    initialTab,
    initialEditPriceIndex,
    enrolledFromCustomer,
    marketingFromCustomer,
  ]);

  const handleEditCustomPrice = (idx: number) => {
    const row = formData.custom_prices[idx];
    if (!row) return;
    setEditingPriceIndex(idx);
    setSelectedItemCode(row.item);
    setSelectedVariantCode(row.variant || '');
    setNewCustomPrice(String(row.custom_price));
    setNewCustomDeliveryPrice(
      row.custom_delivery_price != null ? String(row.custom_delivery_price) : ''
    );
    setPriceError('');
  };

  const handleCancelPriceEdit = () => {
    setEditingPriceIndex(null);
    setSelectedItemCode('');
    setSelectedVariantCode('');
    setNewCustomPrice('');
    setNewCustomDeliveryPrice('');
    setPriceError('');
  };

  const handleAddCustomPrice = () => {
    setPriceError('');
    if (!selectedItemCode) {
      setPriceError('Please select a product first.');
      return;
    }
    const itemObj = catalogItems.find((it: any) => it.name === selectedItemCode);
    if (!itemObj) return;

    const cPrice = parseFloat(newCustomPrice);
    if (isNaN(cPrice) || cPrice < 0) {
      setPriceError('Please enter a valid custom price (0 or greater).');
      return;
    }
    const cDelivPrice =
      showDeliveryPrice && newCustomDeliveryPrice.trim() !== ''
        ? parseFloat(newCustomDeliveryPrice)
        : undefined;
    if (
      showDeliveryPrice &&
      cDelivPrice !== undefined &&
      (isNaN(cDelivPrice) || cDelivPrice < 0)
    ) {
      setPriceError('Custom delivery price cannot be negative.');
      return;
    }

    const variantObj = itemObj.variants?.find((v: any) => v.name === selectedVariantCode);
    const stdPrice = variantObj ? Number(variantObj.price) : Number(itemObj.sale_price || 0);

    const newRow: CustomerItemPrice = {
      item: itemObj.name,
      item_name: itemObj.item_name,
      variant: selectedVariantCode || undefined,
      variant_name: variantObj?.variant_name || undefined,
      standard_price: stdPrice,
      custom_price: cPrice,
      custom_delivery_price: cDelivPrice,
    };

    if (editingPriceIndex !== null && editingPriceIndex >= 0) {
      setFormData((prev) => {
        const updated = [...prev.custom_prices];
        updated[editingPriceIndex] = newRow;
        return { ...prev, custom_prices: updated };
      });
      setEditingPriceIndex(null);
    } else {
      const existingIdx = formData.custom_prices.findIndex(
        (p) => p.item === itemObj.name && (p.variant || '') === (selectedVariantCode || '')
      );
      if (existingIdx >= 0) {
        setFormData((prev) => {
          const updated = [...prev.custom_prices];
          updated[existingIdx] = newRow;
          return { ...prev, custom_prices: updated };
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          custom_prices: [...prev.custom_prices, newRow],
        }));
      }
    }

    setSelectedItemCode('');
    setSelectedVariantCode('');
    setNewCustomPrice('');
    setNewCustomDeliveryPrice('');
    setPriceItemSearch('');
  };

  const handleRemoveCustomPrice = (idx: number) => {
    if (editingPriceIndex === idx) {
      handleCancelPriceEdit();
    }
    setFormData((prev) => ({
      ...prev,
      custom_prices: prev.custom_prices.filter((_, i) => i !== idx),
    }));
  };

  const saveMutation = useMutation({
    mutationFn: (data: unknown) => {
      const payload = editingCustomer
        ? { ...(data as Record<string, unknown>), name: editingCustomer.name, _action: 'update' }
        : { ...(data as Record<string, unknown>), _action: 'create' };
      return createCustomerRouted(payload);
    },
    onSuccess: async (savedCustomer: any) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer-dashboard'] });
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      qc.invalidateQueries({ queryKey: ['posCustomersSearch'] });
      qc.invalidateQueries({ queryKey: ['posCustomerSelect'] });

      try {
        const custName = editingCustomer?.name || savedCustomer?.name;
        if (custName) {
          const db = companyDb();
          const existing = await db.catalog_customers.get(custName);
          if (existing) {
            await db.catalog_customers.put({
              ...existing,
              custom_prices: formData.custom_prices,
            });
          }
        }
      } catch {
        /* ignore offline db sync error */
      }

      toast.success(
        editingCustomer
          ? t('customers.updated', 'Customer updated successfully')
          : t('customers.created', 'Customer created successfully')
      );
      onSaved?.(savedCustomer);
      onClose();
    },
    onError: (err: any) => {
      let msg = err.message || 'Failed to save customer';
      if (err.response?.data?._server_messages) {
        try {
          msg = JSON.parse(JSON.parse(err.response.data._server_messages)[0]).message;
        } catch {
          /* ignore */
        }
      }
      toast.error(msg);
    },
  });

  const validate = (data: typeof formData): FormErrors => {
    const newErrors: FormErrors = {};
    if (!data.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required.';
    }
    const mobileErr = validatePhone(data.mobile, data.country, { required: false });
    if (mobileErr) newErrors.mobile = mobileErr;
    if (data.email.trim() && !EMAIL_REGEX.test(data.email.trim())) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (isPakistan(data.country)) {
      const cnicNtnVal = (data.cnic || data.ntn || '').trim();
      const digits = cnicNtnVal.replace(/\D/g, '');
      if (cnicNtnVal && digits.length !== 13 && digits.length !== 8) {
        newErrors.cnic = 'CNIC must be 13 digits or NTN must be 8 digits.';
      }
    }
    if (canSetAccountLimit && data.credit_limit !== '' && Number(data.credit_limit) < 0) {
      newErrors.credit_limit = t('customers.accountLimitNegative', 'Account limit cannot be negative.');
    }
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setActiveCustomerTab('basic');
      return;
    }

    const cnicDigits = formData.cnic ? formData.cnic.replace(/\D/g, '') : '';
    const ntnDigits = formData.ntn ? formData.ntn.replace(/\D/g, '') : '';
    const openingBalance = parseFloat(formData.opening_balance) || 0;
    if (!editingCustomer && openingBalance < 0) {
      setErrors({ opening_balance: 'Opening balance cannot be negative.' });
      setActiveCustomerTab('basic');
      return;
    }

    const {
      opening_balance: _openingRaw,
      credit_limit: creditLimitRaw,
      join_loyalty,
      marketing_consent,
      ...formFields
    } = formData;
    const payload: Record<string, unknown> = {
      ...formFields,
      country: defaultCountry,
      cnic: cnicDigits,
      ntn: ntnDigits,
      mobile: formData.mobile.trim() ? normalizePhone(formData.mobile, defaultCountry) : '',
      custom_prices: formData.custom_prices,
      join_loyalty: join_loyalty ? 1 : 0,
      marketing_consent: join_loyalty && marketing_consent ? 1 : 0,
    };
    if (canSetAccountLimit) {
      payload.credit_limit = Math.max(0, parseFloat(creditLimitRaw) || 0);
    }
    if (!editingCustomer && openingBalance > 0) {
      payload.opening_balance = openingBalance;
    }
    saveMutation.mutate(payload);
  };

  const filteredCatalogItems = useMemo(() => {
    if (!priceItemSearch.trim()) return catalogItems.slice(0, 80);
    const q = priceItemSearch.toLowerCase();
    return catalogItems
      .filter((it: any) =>
        (it.item_name || '').toLowerCase().includes(q) ||
        (it.name || '').toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [catalogItems, priceItemSearch]);

  if (!isOpen) return null;

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fade-in">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-2xl sm:max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
              {editingCustomer ? t('customers.edit') : t('customers.add')}
            </h3>
            {editingCustomer && (
              <p className="text-xs text-gray-500 font-mono mt-0.5">
                {editingCustomer.name}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50/70 px-5 pt-2.5 gap-4 shrink-0">
          <button
            type="button"
            onClick={() => setActiveCustomerTab('basic')}
            className={`pb-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
              activeCustomerTab === 'basic'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>{t('customers.customerDetails', 'Customer Details')}</span>
          </button>
          {!isCashierLevel(role) && (
            <button
              type="button"
              onClick={() => setActiveCustomerTab('prices')}
              className={`pb-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
                activeCustomerTab === 'prices'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Tag className="w-3.5 h-3.5" />
              <span>{t('customers.specialPrices', 'Special Product Prices')}</span>
              {formData.custom_prices.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-primary-100 text-primary-800 font-bold">
                  {formData.custom_prices.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Error summary */}
        {hasErrors && (
          <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 shrink-0">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <ul className="text-sm text-red-600 space-y-0.5">
              {Object.values(errors).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden" noValidate>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {activeCustomerTab === 'basic' && (
              <div className="space-y-5">
                {/* Identity */}
                <section className="space-y-3">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {t('customers.sectionIdentity', 'Identity')}
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('customers.customerName')} *
                    </label>
                    <input
                      required
                      placeholder={t('customers.customerNamePlaceholder', 'e.g. John Smith')}
                      className={`input-field ${errors.customer_name ? 'border-red-400 focus:ring-red-300' : ''}`}
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    />
                    {errors.customer_name && (
                      <p className="text-xs text-red-500 mt-1">{errors.customer_name}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('customers.customerType')} *
                      </label>
                      <select
                        className="input-field"
                        value={formData.customer_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customer_type: e.target.value as 'Retailer' | 'Wholesale' | 'Distributor',
                          })
                        }
                      >
                        <option value="Retailer">{t('pos.retailer')}</option>
                        <option value="Wholesale">{t('pos.wholesale')}</option>
                        <option value="Distributor">{t('pos.distributor')}</option>
                      </select>
                    </div>
                    {canSetAccountLimit && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('customers.accountLimit', 'Account Limit')} ({getCurrencySymbol()})
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={`input-field ${errors.credit_limit ? 'border-red-400 focus:ring-red-300' : ''}`}
                          value={formData.credit_limit}
                          onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === '-') e.preventDefault();
                          }}
                          placeholder="0"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                          {t(
                            'customers.accountLimitHelp',
                            '0 = unlimited credit. Set a limit to warn on credit sales above this amount.',
                          )}
                        </p>
                        {errors.credit_limit && (
                          <p className="text-xs text-red-500 mt-1">{errors.credit_limit}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="customer_form_is_default"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t('customers.defaultPosCustomer')}
                    </span>
                  </label>
                  {!editingCustomer?.is_walk_in && (
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                        Loyalty
                      </p>
                      <label className="inline-flex items-start gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={formData.join_loyalty || enrolledFromCustomer}
                          disabled={enrolledFromCustomer}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              join_loyalty: e.target.checked,
                              marketing_consent: e.target.checked ? formData.marketing_consent : false,
                            })
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4 mt-0.5"
                        />
                        <span className="text-sm text-gray-800">
                          {enrolledFromCustomer
                            ? 'Enrolled in loyalty programme'
                            : 'Enrol in loyalty programme (accept T&Cs)'}
                        </span>
                      </label>
                      <label
                        className={`inline-flex items-start gap-2 select-none ${
                          formData.join_loyalty ? 'cursor-pointer text-gray-800' : 'text-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={!formData.join_loyalty}
                          checked={formData.marketing_consent}
                          onChange={(e) =>
                            setFormData({ ...formData, marketing_consent: e.target.checked })
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4 mt-0.5"
                        />
                        <span className="text-sm">Marketing consent (optional)</span>
                      </label>
                    </div>
                  )}
                </section>

                {/* Contact */}
                <section className="space-y-3 pt-1 border-t border-gray-100">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 pt-3">
                    {t('customers.sectionContact', 'Contact')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.mobile')}
                      </label>
                      <input
                        placeholder={
                          isPakistan(formData.country) ? '03XXXXXXXXX' : t('customers.phonePlaceholder')
                        }
                        maxLength={isPakistan(formData.country) ? 11 : 15}
                        className={`input-field ${errors.mobile ? 'border-red-400 focus:ring-red-300' : ''}`}
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mobile: sanitizePhoneInput(e.target.value, formData.country),
                          })
                        }
                      />
                      {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.email')}
                      </label>
                      <input
                        type="email"
                        className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-300' : ''}`}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    {(isPakistan(formData.country) || isGcc(formData.country)) && (
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isGcc(formData.country) ? t('customers.taxIdLabelSa') : t('customers.cnicNtn')}
                        </label>
                        <input
                          placeholder={
                            isGcc(formData.country)
                              ? t('customers.taxIdPlaceholderSa')
                              : 'e.g. 35201-1234567-1 or 1234567-8'
                          }
                          maxLength={15}
                          className={`input-field ${errors.cnic ? 'border-red-400 focus:ring-red-300' : ''}`}
                          value={formatCnicOrNtn(formData.cnic || formData.ntn || '')}
                          onChange={(e) => {
                            const rawVal = e.target.value;
                            const formatted = formatCnicOrNtn(rawVal);
                            const digits = rawVal.replace(/\D/g, '');
                            if (digits.length > 8) {
                              setFormData({ ...formData, cnic: formatted, ntn: '' });
                            } else {
                              setFormData({ ...formData, ntn: formatted, cnic: '' });
                            }
                          }}
                        />
                        {errors.cnic && <p className="text-xs text-red-500 mt-1">{errors.cnic}</p>}
                      </div>
                    )}
                  </div>
                </section>

                {/* Address */}
                <section className="space-y-3 pt-1 border-t border-gray-100">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 pt-3">
                    {t('customers.sectionAddress', 'Address')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.country === 'US' ? t('common.zipCode') : t('common.postalCode')}
                      </label>
                      {getCountry(formData.country).usePostcodeLookup ? (
                        <UkPostcodeLookup
                          postalCode={formData.postal_code}
                          onPostalCodeChange={(v) => {
                            setPostcodeError('');
                            setFormData((prev) => ({ ...prev, postal_code: v }));
                          }}
                          onFill={(fields) => {
                            setPostcodeError('');
                            setFormData((prev) => ({
                              ...prev,
                              postal_code: fields.postal_code,
                              province: fields.province || prev.province,
                              city: fields.city || prev.city,
                              address: fields.address !== undefined ? fields.address : prev.address,
                            }));
                          }}
                        />
                      ) : (
                        <input
                          className="input-field"
                          value={formData.postal_code}
                          onChange={(e) => {
                            setPostcodeError('');
                            setFormData({ ...formData, postal_code: e.target.value });
                          }}
                        />
                      )}
                      {postcodeError && <p className="text-xs text-red-500 mt-1">{postcodeError}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.city')}
                      </label>
                      {getCountry(formData.country).useCityMaster ? (
                        <select
                          className="input-field"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        >
                          <option value="">{t('common.select')}</option>
                          {cities.map((c) => (
                            <option key={c.name} value={c.name}>
                              {c.city}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="input-field"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        />
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.region')}
                      </label>
                      <select
                        className="input-field"
                        value={formData.province}
                        onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                      >
                        <option value="">{t('common.select')}</option>
                        {getRegionsForCountry(formData.country).map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.address', 'Street Address')}
                      </label>
                      <textarea
                        rows={2}
                        className="input-field"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder={t('customers.addressPlaceholder', 'e.g. 10 Downing Street, Flat 4')}
                      />
                    </div>
                  </div>
                </section>

                {/* Opening balance (create only) */}
                {!editingCustomer && (
                  <section className="space-y-3 pt-1 border-t border-gray-100">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 pt-3">
                      {t('customers.sectionOpening', 'Opening')}
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('customers.openingBalance')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input-field"
                        placeholder={t('customers.openingBalanceHelp')}
                        value={formData.opening_balance}
                        onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === '-') e.preventDefault();
                        }}
                      />
                      {errors.opening_balance && (
                        <p className="text-xs text-red-500 mt-1">{errors.opening_balance}</p>
                      )}
                    </div>
                  </section>
                )}
              </div>
            )}

            {/* Custom Prices Tab */}
            {activeCustomerTab === 'prices' && (
              <div className="space-y-4">
                <div className="bg-primary-50/50 border border-primary-100 rounded-xl p-3.5 sm:p-4 text-xs text-primary-900 flex items-start gap-3">
                  <Tag className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-primary-900">
                      Customer-Specific Agreed Pricing
                    </p>
                    <p className="text-primary-700 text-[11px] mt-0.5">
                      Configure custom rates for specific items. When this customer is selected in POS or Sales, these prices automatically replace the standard catalog price.
                    </p>
                  </div>
                </div>

                {/* Add/Edit Price Form Card */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 sm:p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Plus className="w-3.5 h-3.5 text-primary-600" />
                      {editingPriceIndex !== null ? 'Edit Price Rule' : 'Add Price Rule'}
                    </span>
                    {editingPriceIndex !== null && (
                      <button
                        type="button"
                        onClick={handleCancelPriceEdit}
                        className="text-[11px] text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>

                  {priceError && (
                    <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{priceError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Product Search & Select */}
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Select Product *
                      </label>
                      <div className="flex gap-2 mb-1.5">
                        <div className="relative flex-1">
                          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Filter product by name or SKU…"
                            value={priceItemSearch}
                            onChange={(e) => setPriceItemSearch(e.target.value)}
                            className="input-field text-xs pl-8 py-1.5"
                          />
                        </div>
                      </div>
                      <select
                        value={selectedItemCode}
                        onChange={(e) => {
                          const itCode = e.target.value;
                          setSelectedItemCode(itCode);
                          setSelectedVariantCode('');
                          const match = catalogItems.find((x: any) => x.name === itCode);
                          if (match && editingPriceIndex === null) {
                            setNewCustomPrice(String(match.sale_price || ''));
                            if (showDeliveryPrice && match.delivery_price != null) {
                              setNewCustomDeliveryPrice(String(match.delivery_price));
                            }
                          }
                        }}
                        className="input-field text-xs"
                      >
                        <option value="">-- Choose a Product --</option>
                        {filteredCatalogItems.map((it: any) => (
                          <option key={it.name} value={it.name}>
                            {it.item_name} ({it.name}) — Standard:{' '}
                            {getCurrencySymbol()}
                            {Number(it.sale_price || 0).toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Variant dropdown if variants exist */}
                    {(() => {
                      const selItem = catalogItems.find((it: any) => it.name === selectedItemCode);
                      if (selItem && selItem.variants && selItem.variants.length > 0) {
                        return (
                          <div className="col-span-1 sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Variant (Optional)
                            </label>
                            <select
                              value={selectedVariantCode}
                              onChange={(e) => {
                                const vCode = e.target.value;
                                setSelectedVariantCode(vCode);
                                const vMatch = selItem.variants?.find((v: any) => v.name === vCode);
                                if (vMatch && editingPriceIndex === null) {
                                  setNewCustomPrice(String(vMatch.price || selItem.sale_price || ''));
                                }
                              }}
                              className="input-field text-xs"
                            >
                              <option value="">All Variants / Base Product</option>
                              {selItem.variants.map((v: any) => (
                                <option key={v.name} value={v.name}>
                                  {v.variant_name} — Price: {getCurrencySymbol()}
                                  {Number(v.price || 0).toFixed(2)}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Custom Price */}
                    <div className={showDeliveryPrice ? 'col-span-1' : 'col-span-1 sm:col-span-2'}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Custom Price ({getCurrencySymbol()}) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newCustomPrice}
                        onChange={(e) => setNewCustomPrice(e.target.value)}
                        placeholder="e.g. 9.50"
                        className="input-field text-xs font-mono font-semibold"
                        onKeyDown={(e) => {
                          if (e.key === '-') e.preventDefault();
                        }}
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">Applied at checkout in POS.</p>
                    </div>

                    {/* Custom Delivery Price */}
                    {showDeliveryPrice && (
                      <div className="col-span-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Custom Delivery Price ({getCurrencySymbol()})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newCustomDeliveryPrice}
                          onChange={(e) => setNewCustomDeliveryPrice(e.target.value)}
                          placeholder="Optional (e.g. 10.50)"
                          className="input-field text-xs font-mono"
                          onKeyDown={(e) => {
                            if (e.key === '-') e.preventDefault();
                          }}
                        />
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Used for delivery orders when preset mode is on.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    {editingPriceIndex !== null ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200 flex items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-amber-600" />
                        Editing rule #{editingPriceIndex + 1}
                      </span>
                    ) : (
                      <span />
                    )}
                    <div className="flex items-center gap-2">
                      {editingPriceIndex !== null && (
                        <button
                          type="button"
                          onClick={handleCancelPriceEdit}
                          className="btn-secondary text-xs py-1.5 px-3"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleAddCustomPrice}
                        disabled={!selectedItemCode}
                        className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {editingPriceIndex !== null ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            <span>Update Price Rule</span>
                          </>
                        ) : (
                          <>
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add Price Rule</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Special Prices Table */}
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
                  <div className="bg-gray-50/80 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">
                      Configured Special Prices ({formData.custom_prices.length})
                    </span>
                  </div>

                  {formData.custom_prices.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <Tag className="w-8 h-8 mx-auto mb-2 opacity-40 text-gray-400" />
                      <p className="text-xs font-medium text-gray-600">
                        No special product prices configured
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Use the section above to add negotiated prices for this customer.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-50/50 text-gray-500 border-b border-gray-100 text-[11px] uppercase tracking-wider sticky top-0">
                          <tr>
                            <th className="px-3.5 py-2">Product</th>
                            <th className="px-3 py-2 text-right">Standard</th>
                            <th className="px-3 py-2 text-right text-emerald-700">Custom Price</th>
                            {showDeliveryPrice && (
                              <th className="px-3 py-2 text-right text-blue-700">
                                Custom Delivery Price
                              </th>
                            )}
                            <th className="px-3 py-2 text-center w-20">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {formData.custom_prices.map((row, idx) => (
                            <tr
                              key={`${row.item}_${row.variant || ''}_${idx}`}
                              className="hover:bg-gray-50/80 transition-colors"
                            >
                              <td className="px-3.5 py-2.5">
                                <div className="font-medium text-gray-900">
                                  {row.item_name || row.item}
                                </div>
                                <div className="text-[10px] text-gray-500 font-mono">
                                  {row.item} {row.variant_name ? `• ${row.variant_name}` : ''}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-gray-500">
                                {row.standard_price != null
                                  ? `${getCurrencySymbol()}${Number(row.standard_price).toFixed(2)}`
                                  : '—'}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-700">
                                {getCurrencySymbol()}
                                {Number(row.custom_price).toFixed(2)}
                              </td>
                              {showDeliveryPrice && (
                                <td className="px-3 py-2.5 text-right font-mono text-blue-700">
                                  {row.custom_delivery_price != null &&
                                  row.custom_delivery_price > 0
                                    ? `${getCurrencySymbol()}${Number(
                                        row.custom_delivery_price
                                      ).toFixed(2)}`
                                    : '—'}
                                </td>
                              )}
                              <td className="px-3 py-2.5 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleEditCustomPrice(idx)}
                                    className="p-1 text-gray-400 hover:text-primary-600 rounded hover:bg-primary-50 transition-colors"
                                    title="Edit this price rule"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCustomPrice(idx)}
                                    className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                                    title="Remove this custom price"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 items-center">
            {editingCustomer && isAdmin && !editingCustomer.is_walk_in && !editingCustomer.is_default && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saveMutation.isPending || deleteMutation.isPending}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors sm:mr-auto"
              >
                <Trash2 className="w-4 h-4" />
                {t('common.delete')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary w-full sm:w-auto sm:min-w-[100px]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || deleteMutation.isPending}
              className="btn-primary w-full sm:w-auto sm:min-w-[140px]"
            >
              {saveMutation.isPending
                ? t('common.saving')
                : editingCustomer
                  ? t('common.update')
                  : t('customers.create')}
            </button>
          </div>
        </form>
      </div>

      {showDeleteConfirm && editingCustomer && (
        <ConfirmModal
          title={t('confirmDelete.title', 'Confirm Delete')}
          message={t('confirmDelete.message', {
            label: editingCustomer.customer_name,
            defaultValue: `Are you sure you want to delete "${editingCustomer.customer_name}"? This action is permanent and cannot be undone.`,
          })}
          confirmText={t('common.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onClose={() => {
            if (!deleteMutation.isPending) {
              setShowDeleteConfirm(false);
            }
          }}
          type="danger"
        />
      )}

      {deleteError && (
        <ConfirmModal
          title={t('confirmDelete.cannotDelete', 'Cannot Delete')}
          message={deleteError}
          confirmText={t('confirmDelete.ok', 'OK')}
          hideCancel
          onConfirm={() => setDeleteError(null)}
          onClose={() => setDeleteError(null)}
          type="warning"
        />
      )}
    </div>
  );
}
